/**
 * Marketing-site legal pages — wrapped in MarketingLayout for consistent
 * navigation/footer. /privacy-policy and /terms-and-conditions reuse the
 * existing legal copy via deep-link redirects; /refund-policy and
 * /content-takedown-policy are new static pages with full legal content.
 */
import { Link } from 'react-router-dom';
import { ShieldCheck, FileText, RefreshCw, Trash2, Mail } from 'lucide-react';
import MarketingLayout from './shared/MarketingLayout';
import { Section } from './shared/blocks';
import SEO from './shared/SEO';
import { BRAND } from './content';

const LAST_UPDATED = 'February 2026';
const COMPANY = 'Actanon Technologies';
const SUPPORT_EMAIL = BRAND.supportEmail;

function LegalShell({ title, icon: Icon, eyebrow, children, testid, slug, description, contactEmail = 'support@actanonedu.com' }) {
  return (
    <MarketingLayout>
      <SEO
        title={`${title} | ActanonEdu`}
        description={description || `${title} for ActanonEdu — last updated ${LAST_UPDATED}.`}
        path={`/${slug}`}
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: title, url: `/${slug}` }]}
      />
      <div className="bg-mk-ice/60 dark:bg-mk-navy/40 border-b border-mk-line dark:border-mk-edge" data-testid={testid}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {eyebrow && (
            <div className="text-xs font-semibold uppercase tracking-widest text-mk-blue dark:text-mk-cyan mb-2 flex items-center gap-2">
              {Icon && <Icon className="h-4 w-4" />} {eyebrow}
            </div>
          )}
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{title}</h1>
          <p className="text-sm text-slate-500 mt-3">Last updated: {LAST_UPDATED} · {COMPANY}</p>
          <p className="text-sm text-mk-muted dark:text-slate-300 mt-1">
            Questions about this policy? Email <a href={`mailto:${contactEmail}`} className="text-mk-deep dark:text-mk-cyan font-semibold hover:underline">{contactEmail}</a>
          </p>
        </div>
      </div>
      <Section>
        <article
          className="max-w-3xl mx-auto prose prose-slate dark:prose-invert
            prose-headings:font-bold prose-headings:tracking-tight
            prose-h2:text-xl sm:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3
            prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-2
            prose-strong:text-mk-ink dark:prose-strong:text-white
            prose-p:my-3 prose-ul:my-3 prose-li:my-1
            text-[15px] leading-relaxed"
        >
          {children}
        </article>
        <div className="max-w-3xl mx-auto mt-12 p-5 rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy flex items-start gap-3">
          <Mail className="h-5 w-5 text-mk-blue mt-0.5" />
          <div>
            <div className="font-semibold text-sm">Questions about this policy?</div>
            <a href={`mailto:${contactEmail}`} className="text-sm text-mk-deep dark:text-mk-cyan hover:underline">{contactEmail}</a>
            <span className="text-sm text-slate-500"> · or </span>
            <Link to="/contact" className="text-sm text-mk-deep dark:text-mk-cyan hover:underline">Contact us</Link>
          </div>
        </div>
      </Section>
    </MarketingLayout>
  );
}

// ─── Privacy Policy ───
export function PrivacyPolicyPage() {
  return (
    <LegalShell
      eyebrow="Legal"
      icon={ShieldCheck}
      title="Privacy Policy"
      testid="privacy-policy-page"
      slug="privacy-policy"
      description="ActanonEdu Privacy Policy — how we collect, use, process and protect personal data. India-hosted. AES-256 encryption. Never train AI on your student data."
    >
      <p>This Privacy Policy describes how {COMPANY} (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) collects, uses, processes, and protects personal data when you use ActanonEdu (the &ldquo;Platform&rdquo;). It applies to teachers, school administrators, students, parents, and anyone interacting with the Platform.</p>

      <h2>1. Information we collect</h2>
      <ul>
        <li><strong>Account data</strong>: name, email, mobile number, role (teacher / admin / parent / student), school affiliation.</li>
        <li><strong>Generated content</strong>: lesson plans, worksheets, assessments, and other AI outputs you create. These are stored against your account and not shared with other users without your explicit consent.</li>
        <li><strong>Usage telemetry</strong>: device type, browser, pages visited, generation counts, and feature usage — used to improve product reliability and performance.</li>
        <li><strong>Payment data</strong>: handled by Razorpay; we receive only transaction status and order references, never card numbers.</li>
        <li><strong>Student data</strong> (in School ERP plans): names, classes, attendance, marks, fee status — entered by the school under their own privacy obligations.</li>
      </ul>

      <h2>2. How we use the data</h2>
      <ul>
        <li>To provide and improve the Platform (account, generation, ERP modules).</li>
        <li>To communicate updates, security notices, and (only with consent) marketing.</li>
        <li>To meet legal, accounting, and audit obligations.</li>
        <li>To enforce our Terms and prevent abuse.</li>
      </ul>

      <h2>3. Data dignity commitments</h2>
      <ul>
        <li>Servers are in India. Production data does not leave India without your consent.</li>
        <li>We never use your student data to train AI models — your data stays your data.</li>
        <li>You can request data export or deletion at any time via {SUPPORT_EMAIL}.</li>
        <li>School ERP data is governed by a separate Data Processing Addendum (DPA) on School plans.</li>
      </ul>

      <h2>4. Third-party processors</h2>
      <p>We share data with strictly necessary third-party processors: OpenAI / Anthropic / Google for AI inference (no PII passed when avoidable), Razorpay for payments, AWS / Google Cloud for hosting, and Google Analytics for traffic measurement (anonymised). Each is bound by data protection terms.</p>

      <h2>5. Cookies & analytics</h2>
      <p>We use first-party session cookies for authentication and Google Analytics for product analytics. You can disable analytics in your account preferences.</p>

      <h2>6. Children&apos;s privacy</h2>
      <p>Student data is entered by schools or guardians — never directly by students under 13. Schools are responsible for obtaining parental consent before adding student records.</p>

      <h2>7. Security</h2>
      <p>Data is encrypted in transit (TLS 1.2+) and at rest (AES-256). Access is role-based with audit logs. Security incidents are notified within 72 hours.</p>

      <h2>8. Your rights</h2>
      <p>You have the right to access, correct, port, or delete your personal data. Contact {SUPPORT_EMAIL} to exercise any right; we respond within 30 days.</p>

      <h2>9. Changes to this policy</h2>
      <p>We will notify users by email and in-app banner at least 14 days before material changes take effect.</p>

      <p className="text-sm text-slate-500">Governed by the laws of India. Disputes will be resolved exclusively in courts of competent jurisdiction in India.</p>
    </LegalShell>
  );
}

// ─── Terms & Conditions ───
export function TermsAndConditionsPage() {
  return (
    <LegalShell
      eyebrow="Legal"
      icon={FileText}
      title="Terms & Conditions"
      testid="terms-page"
      slug="terms-and-conditions"
      description="ActanonEdu Terms & Conditions — governing your use of the Platform. Covers eligibility, acceptable use, payments, intellectual property, and termination."
    >
      <p>These Terms & Conditions govern your use of ActanonEdu (the &ldquo;Platform&rdquo;), operated by {COMPANY}. By using the Platform you agree to be bound by these terms.</p>

      <h2>1. Eligibility</h2>
      <p>You must be at least 18 years old to create an account, or have explicit consent from a parent / guardian / school administrator if you are younger.</p>

      <h2>2. Account responsibility</h2>
      <ul>
        <li>You are responsible for keeping your credentials confidential and for all activity under your account.</li>
        <li>Notify us immediately of any unauthorised use at {SUPPORT_EMAIL}.</li>
      </ul>

      <h2>3. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Platform to generate content that is illegal, hateful, deceptive, defamatory, or sexually explicit involving minors.</li>
        <li>Reverse-engineer, scrape, or attempt to extract our prompts, models, or other proprietary information.</li>
        <li>Resell, white-label, or sublicense access to the Platform without a written agreement.</li>
        <li>Bypass usage limits, rate limits, or watermarking on free-tier outputs.</li>
      </ul>

      <h2>4. Generated content</h2>
      <p>AI-generated content is provided as-is. You are responsible for reviewing and verifying all generated content before using it with students. We make no warranty that AI outputs are free of error.</p>

      <h2>5. Payments & subscriptions</h2>
      <ul>
        <li>Subscriptions are billed monthly or annually via Razorpay. Refunds are governed by our Refund Policy.</li>
        <li>Failure to pay may result in feature restriction or account suspension.</li>
      </ul>

      <h2>6. Intellectual property</h2>
      <p>The Platform (including its UI, code, prompts, models, and branding) is owned by {COMPANY}. You retain ownership of the content you generate. We claim a non-exclusive license to use anonymised, aggregated metadata for product improvement.</p>

      <h2>7. School Pilot Program</h2>
      <p>The 30-day School Pilot Program is governed by additional terms set out on the <Link to="/school-pilot-program">School Pilot Program page</Link>.</p>

      <h2>8. Limitation of liability</h2>
      <p>To the maximum extent permitted by law, {COMPANY} shall not be liable for indirect, incidental, or consequential damages. Total liability shall not exceed the fees paid by you in the 12 months preceding the claim.</p>

      <h2>9. Termination</h2>
      <p>We may suspend or terminate your account for breach of these Terms, with reasonable notice except in cases of severe abuse. You may terminate your account at any time via your account settings.</p>

      <h2>10. Governing law</h2>
      <p>These Terms are governed by the laws of India. Disputes will be resolved exclusively in courts of competent jurisdiction in India.</p>
    </LegalShell>
  );
}

// ─── Refund Policy ───
export function RefundPolicyPage() {
  return (
    <LegalShell
      eyebrow="Legal"
      icon={RefreshCw}
      title="Cancellation & Refund Policy"
      testid="refund-policy-page"
      slug="refund-policy"
      description="ActanonEdu Cancellation & Refund Policy — 7-day refund window for Pro plans, pro-rata refunds within 30 days for annual plans, full School Pilot terms."
    >
      <p>We want every teacher and school to feel confident upgrading to a paid plan. This Refund Policy explains when and how refunds are issued.</p>

      <h2>1. Pro (Individual Teacher) plans</h2>
      <ul>
        <li><strong>7-day refund window:</strong> Full refund if you raise a ticket within 7 calendar days of payment explaining why the Platform did not meet your expectation.</li>
        <li><strong>Auto-renewals:</strong> Cancel any time from your account settings; cancellations take effect at the end of the current billing period — pro-rata refunds are not offered after the 7-day window.</li>
        <li><strong>Annual plans:</strong> Pro-rata refund minus 10% admin fee if cancelled within 30 days of purchase. No refund thereafter.</li>
      </ul>

      <h2>2. School plans</h2>
      <ul>
        <li>School plans are typically annual contracts. Refunds are governed by the signed Service Agreement, which generally includes a 30-day satisfaction guarantee from the first teacher activation.</li>
        <li>For pilot-to-paid conversions, the first month is fully refundable if cancelled within 30 days of go-live.</li>
      </ul>

      <h2>3. How to request a refund</h2>
      <ol>
        <li>Email {SUPPORT_EMAIL} from your registered account email.</li>
        <li>Include your name, account email, payment reference (Razorpay order ID), and a brief reason.</li>
        <li>We acknowledge within 24 working hours and process eligible refunds within 7–10 working days back to the original payment method.</li>
      </ol>

      <h2>4. Non-refundable items</h2>
      <ul>
        <li>One-time setup or onboarding fees on School Enterprise plans.</li>
        <li>Custom integration work delivered.</li>
        <li>Generation usage exceeding fair-use thresholds.</li>
      </ul>

      <h2>5. Chargebacks</h2>
      <p>Please contact us before initiating a chargeback — most issues are resolved within a single email exchange. Unjustified chargebacks may result in account suspension.</p>

      <h2>6. Pilot Program</h2>
      <p>The School Pilot Program is free. There is nothing to refund. Pilot participation does not auto-convert into a paid plan.</p>
    </LegalShell>
  );
}

// ─── Content Takedown Policy ───
export function ContentTakedownPolicyPage() {
  return (
    <LegalShell
      eyebrow="Legal"
      icon={Trash2}
      title="Content Takedown Policy"
      testid="content-takedown-page"
      slug="content-takedown-policy"
      description="ActanonEdu Content Takedown Policy — how to request the removal of allegedly infringing or unlawful content. Standard 5-working-day response."
      contactEmail="legal@actanonedu.com"
    >
      <p>If you believe content available on or generated through ActanonEdu infringes your copyright, violates applicable law, or breaches our Terms, you can request a takedown using the process below.</p>

      <h2>1. What qualifies for takedown</h2>
      <ul>
        <li>Content that infringes a copyright, trademark, or other intellectual-property right you own or are authorised to enforce.</li>
        <li>Content that is defamatory, hateful, harassing, or violates Indian law.</li>
        <li>Generated content that incorrectly reproduces verbatim copyrighted material (e.g. textbook excerpts) above fair-use thresholds.</li>
        <li>Personal data published without consent.</li>
      </ul>

      <h2>2. How to submit a takedown request</h2>
      <p>Send an email to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> with the subject line <strong>&ldquo;Content Takedown Request&rdquo;</strong> and include:</p>
      <ul>
        <li>Your full name, organisation, and contact details.</li>
        <li>A description of the work you claim has been infringed (with URLs / files where applicable).</li>
        <li>A description of the allegedly infringing content on ActanonEdu (with URLs / asset IDs / screenshots).</li>
        <li>A statement of good-faith belief that the use is unauthorised.</li>
        <li>A statement, under penalty of perjury, that the information in your notice is accurate and that you are authorised to act on behalf of the rights holder.</li>
        <li>Your electronic or physical signature.</li>
      </ul>

      <h2>3. What happens next</h2>
      <ol>
        <li>We acknowledge receipt within 24 working hours.</li>
        <li>If the request is complete and credible, we remove or restrict access to the content within 5 working days while we investigate.</li>
        <li>We notify the uploader so they have an opportunity to respond.</li>
        <li>If a counter-notice is received, we may restore the content unless you initiate legal proceedings within a reasonable timeframe.</li>
      </ol>

      <h2>4. Bad-faith notices</h2>
      <p>Submitting a knowingly false takedown notice may expose you to liability for damages, including costs and attorneys&apos; fees. Repeat false notices will be blocked.</p>

      <h2>5. Counter-notice process</h2>
      <p>If your content was removed and you believe the removal was a mistake or misidentification, send a counter-notice to {SUPPORT_EMAIL} with: your contact info, identification of the removed content, a statement under penalty of perjury that the removal was a mistake, and your consent to jurisdiction in Indian courts.</p>

      <h2>6. Repeat infringers</h2>
      <p>We terminate the accounts of users who are subject to multiple credible infringement notices.</p>
    </LegalShell>
  );
}
